import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { getUserPublicId } from '@infra/auth';
import { FIRST_PAGE, PICKER_PAGE_SIZE } from '@shared/constants';
import { meAccessQueryKey } from '../access';
import { USAGE_QUERY_KEYS } from '../usage';
import type { OrganizationStatus } from '../access/access.types';
import {
  DEFAULT_ORG_CODE,
  getOrganization,
  listOrganizations,
  createOrganization,
  updateOrganization,
  activateOrganization,
  suspendOrganization,
  deleteOrganization,
  type OrganizationDto,
  type UpdateOrganizationBody,
} from './admin-organizations.api';
import {
  getMyOrganization,
  updateMyOrganization,
  type MyOrganization,
} from './organization.api';

const ORGANIZATIONS_ROOT = ['core', 'organizations'] as const;

/** Every organization query sits under ALL, so one invalidation refreshes lists, pickers and details. */
export const ORGANIZATIONS_QUERY_KEYS = {
  ALL: ORGANIZATIONS_ROOT,
  LIST: (search: string, page: number, limit: number, status = '') =>
    [...ORGANIZATIONS_QUERY_KEYS.ALL, 'list', search, page, limit, status] as const,
  PICKER: (search: string) => [...ORGANIZATIONS_QUERY_KEYS.ALL, 'picker', search] as const,
  DETAIL: (uuid: string) => [...ORGANIZATIONS_QUERY_KEYS.ALL, 'detail', uuid] as const,
  DEFAULT: [...ORGANIZATIONS_ROOT, 'default'] as const,
};

/** The org admin's own organization; under the root so organization invalidations refresh it. */
export const MY_ORGANIZATION_QUERY_KEY = [...ORGANIZATIONS_ROOT, 'mine'] as const;

/** One page of the organizations screen. */
export function useOrganizationListQuery(
  search: string,
  page: number,
  limit: number,
  status: OrganizationStatus | '' = ''
) {
  return useQuery({
    queryKey: ORGANIZATIONS_QUERY_KEYS.LIST(search, page, limit, status),
    queryFn: () =>
      listOrganizations({ search: search || undefined, page, limit, status: status || undefined }),
    placeholderData: keepPreviousData,
  });
}

/** Organizations for a picker, page by page; keeps the previous list on screen while a new search loads. */
export function useOrganizationsQuery(search: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: ORGANIZATIONS_QUERY_KEYS.PICKER(search),
    queryFn: ({ pageParam }) =>
      listOrganizations({ search: search || undefined, page: pageParam, limit: PICKER_PAGE_SIZE }),
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (last) =>
      last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useOrganizationQuery(uuid: string) {
  return useQuery({
    queryKey: ORGANIZATIONS_QUERY_KEYS.DETAIL(uuid),
    queryFn: () => getOrganization(uuid),
    enabled: Boolean(uuid),
  });
}

export function useCreateOrganizationMutation(onSuccess: (record: OrganizationDto) => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createOrganization,
    onSuccess: async (record) => {
      client.setQueryData(ORGANIZATIONS_QUERY_KEYS.DETAIL(record.uuid), record);
      onSuccess(record);
      await client.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEYS.ALL });
    },
  });
}

export function useUpdateOrganizationMutation(
  uuid: string,
  options: {
    onSuccess: (record: OrganizationDto) => void;
    onError: (error: unknown) => void;
  }
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOrganizationBody) => updateOrganization(uuid, body),
    onSuccess: async (record) => {
      client.setQueryData(ORGANIZATIONS_QUERY_KEYS.DETAIL(uuid), record);
      options.onSuccess(record);
      await client.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEYS.ALL });
      // Usage periods are derived from the cycle day.
      await client.invalidateQueries({ queryKey: USAGE_QUERY_KEYS.ALL });
    },
    onError: options.onError,
  });
}

export type OrganizationStatusAction = 'activate' | 'suspend' | 'delete';
export function useOrganizationStatusMutation(
  uuid: string,
  onSuccess: (action: OrganizationStatusAction) => void
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (action: OrganizationStatusAction) =>
      action === 'delete'
        ? deleteOrganization(uuid)
        : action === 'activate'
          ? activateOrganization(uuid)
          : suspendOrganization(uuid),
    onSuccess: async (record, action) => {
      if (action === 'delete')
        client.removeQueries({ queryKey: ORGANIZATIONS_QUERY_KEYS.DETAIL(uuid) });
      else client.setQueryData(ORGANIZATIONS_QUERY_KEYS.DETAIL(uuid), record);
      onSuccess(action);
      await client.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEYS.ALL });
    },
  });
}

/** The organization everyone without another belongs to. Super admin only. */
export function useDefaultOrganizationQuery(enabled = true) {
  return useQuery({
    queryKey: ORGANIZATIONS_QUERY_KEYS.DEFAULT,
    queryFn: async () => {
      const page = await listOrganizations({
        search: DEFAULT_ORG_CODE,
        limit: PICKER_PAGE_SIZE,
      });
      return page.data.find((o) => o.code === DEFAULT_ORG_CODE) ?? null;
    },
    enabled,
  });
}

/** The signed-in org admin's own organization. */
export function useMyOrganizationQuery(enabled = true) {
  return useQuery({ queryKey: MY_ORGANIZATION_QUERY_KEY, queryFn: getMyOrganization, enabled });
}

/** Renames the organization and refreshes the header, which shows the name from the access record. */
export function useRenameMyOrganizationMutation(options?: {
  onSuccess?: (record: MyOrganization) => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => updateMyOrganization(name),
    onSuccess: async (record) => {
      queryClient.setQueryData(MY_ORGANIZATION_QUERY_KEY, record);
      try {
        options?.onSuccess?.(record);
      } finally {
        await queryClient.invalidateQueries({ queryKey: MY_ORGANIZATION_QUERY_KEY });
        await queryClient.invalidateQueries({ queryKey: meAccessQueryKey(getUserPublicId()) });
      }
    },
    onError: options?.onError,
  });
}
